import React from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, X } from 'lucide-react';
import { useBookmarks } from '../contexts/BookmarkContext';
import { internalHref } from '../utils/internalHref';

interface BookmarksDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const calculatorTitles: { [key: string]: string } = {
  '/hull-volume': 'Hull Volume Calculator',
  '/inflatable-volume': 'Inflatable Volume Calculator',
  '/transom-thickness': 'Transom Thickness Calculator',
  '/structural-load': 'Structural Load Calculator',
  '/deck-plank': 'Deck Plank Calculator',
  '/fiberglass-ratio': 'Fiberglass Ratio Calculator',
  '/keel-width': 'Keel Width Calculator',
  '/fabric-strength': 'Fabric Strength Calculator',
  '/seam-reinforcement': 'Seam Reinforcement Calculator',
  '/tube-inflation': 'Tube Inflation Calculator',
  '/center-of-effort': 'Center of Effort Calculator',
  '/mast-ratio': 'Mast Height Ratio Calculator',
  '/righting-moment': 'Righting Moment Calculator',
  '/sheet-load': 'Sheet Load Calculator',
  '/heel-angle': 'Heel Angle Calculator',
  '/sail-area': 'Sail Area Calculator',
  '/keel-gravity': 'Keel Gravity Calculator',
  '/wind-efficiency': 'Wind Efficiency Calculator',
  '/apparent-wind': 'Apparent Wind Calculator',
  '/true-wind': 'True Wind Calculator',
  '/length-beam-ratio': 'Length-Beam Ratio Calculator',
  '/displacement-length': 'Displacement Length Calculator',
  '/waterline-length': 'Waterline Length Calculator',
  '/draft-depth': 'Draft Depth Calculator',
  '/freeboard-height': 'Freeboard Height Calculator',
  '/sail-displacement': 'Sail-Displacement Calculator',
  '/hull-speed': 'Hull Speed Calculator',
  '/prop-pitch': 'Propeller Pitch Calculator',
  '/power-weight': 'Power-Weight Ratio Calculator',
  '/engine-consumption': 'Engine Consumption Calculator',
  '/fuel-economy': 'Fuel Economy Calculator',
  '/range-calculator': 'Range Calculator',
  '/horsepower-speed': 'Horsepower-Speed Calculator',
  '/battery-sizing': 'Battery Sizing Calculator',
  '/solar-requirements': 'Solar Requirements Calculator',
  '/alternator-output': 'Alternator Output Calculator',
  '/voltage-drop': 'Voltage Drop Calculator',
  '/rpm-speed': 'RPM-Speed Calculator',
  '/prop-slip': 'Propeller Slip Calculator',
  '/fuel-volume': 'Fuel Volume Calculator'
};

const BookmarksDrawer: React.FC<BookmarksDrawerProps> = ({ isOpen, onClose }) => {
  const { bookmarks, removeBookmark } = useBookmarks();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-xl">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Bookmark className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Bookmarked Calculators</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-4">
          {bookmarks.length === 0 ? (
            <p className="text-gray-500 text-center">No bookmarks yet</p>
          ) : (
            <ul className="space-y-2">
              {bookmarks.map((path) => (
                <li key={path} className="flex items-center justify-between">
                  <Link
                    to={internalHref(path)}
                    className="text-blue-600 hover:text-blue-700"
                    onClick={onClose}
                  >
                    {calculatorTitles[path] || path}
                  </Link>
                  <button
                    onClick={() => removeBookmark(path)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookmarksDrawer;